import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usuarioService from '../services/UsuarioService';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import PaymentRequired from './PaymentRequired';
import RegisterModal from './RegisterModal';
import ctaCteLogo from '../assets/ctacte/logoCtaCte.png';
import './Login.css';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    type: 'error',
    title: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos
    if (!formData.email || !formData.password) {
      setAlert({
        show: true,
        title: 'Campos incompletos',
        message: 'Por favor completa todos los campos antes de continuar.',
        type: 'warning'
      });
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await usuarioService.login(formData.email, formData.password);
      setPaymentInfo(null);
      const userData = {
        id: response.id,
        email: response.email,
        name: response.displayName,
        displayName: response.displayName,
        token: response.token,
        pictureUrl: response.pictureUrl || '',
        statusCode: response.statusCode,
        idRol: response.idRol,
        nombreRol: response.nombreRol,
        roleName: response.roleName,
        role: response.role,
        idTenant: response.idTenant,
        tenantName: response.tenantName,
        tenantSchemaName: response.tenantSchemaName
      };
      
      await login(userData);
      navigate('/ctacte', { replace: true });
      
    } catch (error) {
      console.error('❌ Error en login:', error);

      if (error?.status === 402 && error?.payload?.pago) {
        setPaymentInfo({
          mensaje: error.payload.mensaje,
          checkoutUrl: error.payload.pago.checkoutUrl,
          monto: error.payload.pago.monto,
          moneda: error.payload.pago.moneda
        });
        return;
      }

      // Mostrar mensaje priorizando el que devuelve el backend
      let errorMessage = error?.message || 'Error al iniciar sesión. Por favor, intenta nuevamente.';

      const lowerMsg = (errorMessage || '').toLowerCase();

      if (lowerMsg.includes('401') || lowerMsg.includes('unauthorized')) {
        errorMessage = 'Email o contraseña incorrectos. Verifica tus credenciales e intenta nuevamente.';
      } else if (lowerMsg.includes('404')) {
        errorMessage = 'Usuario no encontrado. Verifica tu email o solicita acceso al sistema.';
      } else if (lowerMsg.includes('500')) {
        errorMessage = 'Error del servidor. Por favor, intenta más tarde o contacta al administrador.';
      } else if (lowerMsg.includes('network')) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.';
      }
      
      setAlert({
        show: true,
        title: 'Error al iniciar sesión',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleRetry = () => {
    handleSubmit({ preventDefault: () => {} });
  };

  const handleBackToLogin = () => {
    setPaymentInfo(null);
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  return (
    <div className="login-container">
      {/* Background Pattern */}
      <div className="login-background">
        <div className="geometric-pattern"></div>
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="login-content">
        {/* Left Side - Branding */}
        <div className="login-branding">
          <div className="branding-content">
            <div className="branding-text">
              <img
                src={ctaCteLogo}
                alt="CtaCte App"
                className="branding-logo"
              />
              <h1 className="brand-name">CtaCte App</h1>
              <p className="subtitle">
                Gestión de cuenta corriente
              </p>
            </div>

            <div className="features-list">
              <div className="feature-item">
                <div className="feature-icon">
                  <i className="fas fa-robot"></i>
                </div>
                <span>Cuentas y subcuentas organizadas</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <i className="fas fa-arrows-rotate"></i>
                </div>
                <span>Movimientos claros y trazables</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <i className="fas fa-shield-alt"></i>
                </div>
                <span>Acceso seguro por operador</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-form-section">
          <div className="form-container">
            {paymentInfo ? (
              <PaymentRequired
                info={paymentInfo}
                onRetry={handleRetry}
                onBack={handleBackToLogin}
                isRetrying={isLoading}
              />
            ) : (
              <>
                <div className="form-logos-container">
                  <img
                    src={ctaCteLogo}
                    alt="CtaCte App"
                    className="form-logo brand-logo-form"
                  />
                </div>

                <div className="form-header">
                  <h2 className="form-title">Ingresar a CtaCte App</h2>
                  <p className="form-subtitle">
                    Accedé a tu panel de cuenta corriente
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                  <div className="input-group">
                    <label htmlFor="email" className="input-label">
                      Correo Electrónico
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-envelope input-icon"></i>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="tu email"
                        required
                      />
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="password" className="input-label">
                      Contraseña
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-lock input-icon"></i>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={togglePasswordVisibility}
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className={`login-button ${isLoading ? 'loading' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <img
                          src={ctaCteLogo}
                          alt="CtaCte App"
                          className="login-spinner-logo"
                        />
                        <span>Verificando...</span>
                      </>
                    ) : (
                      <>
                        <span>Ingresar</span>
                        <i className="fas fa-arrow-right"></i>
                      </>
                    )}
                  </button>
                </form>

                <button
                  type="button"
                  className="register-account-link"
                  onClick={() => setShowRegister(true)}
                >
                  ¿No tenés cuenta? <strong>Registrate</strong>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Loading Spinner */}
      {isLoading && (
        <LoadingSpinner 
          message="Iniciando sesión..." 
          size="medium" 
          color="primary" 
        />
      )}

      {/* Alert Component */}
      <Alert
        title={alert.title}
        message={alert.message}
        type={alert.type}
        isVisible={alert.show}
        onClose={closeAlert}
      />

      {showRegister && (
        <RegisterModal onClose={() => setShowRegister(false)} />
      )}
    </div>
  );
};

export default Login;
